import { useState } from 'react';
import styled from 'styled-components';
import { Flex } from '@elementary/common-ui/components';
import useDataTestsEnabled from '@elementary/common-ui/hooks/useDataTestsEnabled';
import { WarehouseType } from '@elementary/common-ui/types';
import { CircularProgress, Divider } from '@mui/material';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { AddTestForm, ColumnResource, Resource } from 'shared/types';
import QualityDimensionTabs, {
  TestCatalogFilters,
} from './QualityDimensionTabs';
import TestCardsSection, { CardTemplate } from './TestCardsSection';
import useWizardTestCatalog from './useWizardTestCatalog';

const Wrapper = styled(Flex)`
  height: 100%;
  width: 100%;
`;

type Props = {
  watch: UseFormWatch<AddTestForm>;
  setValue: UseFormSetValue<AddTestForm>;
  tableResourcesData: Resource[];
  columnsResourcesData: ColumnResource[];
  warehouseType?: WarehouseType;
};

const WizardTestStage = ({
  watch,
  setValue,
  tableResourcesData,
  columnsResourcesData,
  warehouseType,
}: Props) => {
  const isDataTestsEnabled = useDataTestsEnabled();
  const [testCatalogFilters, setTestCatalogFilters] =
    useState<TestCatalogFilters>(() => ({
      quickFilter: 'all',
      ...(isDataTestsEnabled && { sourceFilter: 'elementary cloud' }),
    }));

  const resourceType = watch('resourceType');
  const selectedResourcesIds = watch('resources');

  const {
    isLoading,
    elementaryTests,
    testTemplatesData,
    selectedTableResourcesData,
    shouldConnectCodeRepo,
  } = useWizardTestCatalog({
    resourceType,
    selectedResourceIds: selectedResourcesIds ?? [],
    tableResourcesData,
    columnsResourcesData,
    warehouseType,
  });

  const selectedTemplateId =
    watch('testType') === 'yamlTest'
      ? watch('yamlTemplateName')
      : watch('testType');

  const handleCardClick = (template: CardTemplate) => {
    const isYamlTemplate = template.testType === 'yamlTest';
    const clickedTemplateId = isYamlTemplate ? template.key : template.testType;

    const isDeselect =
      !!selectedTemplateId && clickedTemplateId === selectedTemplateId;

    if (isDeselect) {
      setValue('testType', '');
      setValue('yamlTemplateName', '');
    } else {
      setValue('testType', template.testType);
      if (isYamlTemplate) {
        setValue('yamlTemplateName', template.key);
      } else {
        setValue('yamlTemplateName', '');
      }
    }
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <Wrapper divider={<Divider orientation="vertical" light />}>
      <QualityDimensionTabs
        value={testCatalogFilters}
        onChange={setTestCatalogFilters}
        elementaryTests={elementaryTests}
        testsTemplatesData={testTemplatesData}
      />
      <TestCardsSection
        testCatalogFilters={testCatalogFilters}
        selectedTemplateId={selectedTemplateId}
        onCardClick={handleCardClick}
        elementaryTests={elementaryTests}
        testsTemplatesData={testTemplatesData}
        shouldConnectCodeRepo={shouldConnectCodeRepo}
        selectedTableResourcesData={selectedTableResourcesData}
        selectedTargetsCount={selectedResourcesIds?.length ?? 0}
        warehouseType={warehouseType}
      />
    </Wrapper>
  );
};

export default WizardTestStage;
